import { type FC } from 'react';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import { type BrandConfig, Brand } from 'AppBranding';
import { twMerge } from 'tailwind-merge';
import { useAudioVisualizer, useTrackAudioData } from '../utils';
import { type SupportedColors, type AudioVisualizerConfig } from '../TutorBotVoiceAgent.types';

type AudioVisualizerPresentationProps = AudioVisualizerConfig & {
    getUserAudioData: ReturnType<typeof useTrackAudioData>['getUserAudioData'];
    getAgentAudioData: ReturnType<typeof useTrackAudioData>['getAgentAudioData'];
    color: SupportedColors | undefined;
    size?: 'xs';
};

const AudioVisualizerTheme = {
    coral: {
        // If you find yourself searching for these colors,
        // we should ask if they should be named and added to our common colors.
        inner: '#FF3850', // Vibrant coral-red
        outer: '#FF7676', // Soft luminous coral-pink
    },
    blue: {
        inner: '#2B44FF', // Rich royal blue
        outer: '#60A5FA', // Light sky blue
    },
    purple: {
        inner: '#8B31FF',
        outer: '#B87FFF',
    },
} as const;

const getAudioVisualizerTheme = ({ brandConfig, color }: { brandConfig: BrandConfig; color: 'purple' | undefined }) => {
    let key: keyof typeof AudioVisualizerTheme;
    if (color === 'purple') {
        key = 'purple';
    } else {
        key = brandConfig.branding === Brand.valar ? 'blue' : 'coral';
    }
    return AudioVisualizerTheme[key];
};

// used in ConnectedLiveKitRoomUi story
export const AudioVisualizerWrapper = ({ children, size }: { children: React.ReactNode; size?: 'xs' }) => (
    <div className={twMerge('flex h-full flex-col items-center justify-center')}>
        <div className="relative mx-auto flex animate-fade-in flex-col items-center justify-center overflow-visible">
            <div
                style={{
                    width: size === 'xs' ? '30px' : '282px',
                    height: size === 'xs' ? '30px' : '282px',
                }}
            >
                {children}
            </div>
        </div>
    </div>
);

export const AudioVisualizerPresentation: FC<AudioVisualizerPresentationProps> = ({
    getUserAudioData,
    getAgentAudioData,
    color,
    size,
}) => {
    const brandConfig = useTargetBrandConfig();
    const gradientColors = getAudioVisualizerTheme({ brandConfig, color });

    const canvasRef = useAudioVisualizer({
        size,
        gradientColors,
        getUserAudioData,
        getAgentAudioData,
    });

    return (
        <AudioVisualizerWrapper size={size}>
            <canvas ref={canvasRef} />
        </AudioVisualizerWrapper>
    );
};

export const AudioVisualizer: FC<{ color: SupportedColors | undefined; size?: 'xs' }> = ({ color, size }) => {
    const { getUserAudioData, getAgentAudioData } = useTrackAudioData();

    return (
        <AudioVisualizerPresentation
            color={color}
            size={size}
            getUserAudioData={getUserAudioData}
            getAgentAudioData={getAgentAudioData}
        />
    );
};
